import React, { memo, useCallback } from 'react';
import { Box, Avatar, IconButton } from '@mui/material';
import { SmartToy, PersonOutlineOutlined, Build as BuildIcon, ContentCopy } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import MarkupMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/MarkupMAIQ';
import { Message, MessageCardVariantType, MessageCardProps } from '../types';

const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  alignItems: 'flex-start',
  marginRight: theme.spacing(2),
}));

const MessageRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  flexDirection: 'row',
  maxWidth: 'calc(100% - 60px)',
  minWidth: 0,
  boxSizing: 'border-box',
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => !['isUser', 'processingStatus'].includes(prop as string),
})<{ isUser: boolean; processingStatus?: string }>(({ theme, isUser, processingStatus }) => {
  let backgroundColor = isUser ? theme.palette.secondary.main : '#f5f5f5';
  let borderColor = 'transparent';
  let textColor = isUser ? 'white' : '#1f2937';
  let animation = 'none';
  
  if (!isUser && processingStatus && processingStatus !== 'idle') {
    backgroundColor = '#fff3cd';
    borderColor = '#ffc107';
    textColor = '#856404';
    animation = 'pulse 2s infinite';
  }

  return {
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.5),
    maxWidth: 'calc(100% - 120px)',
    minWidth: 'fit-content',
    width: 'fit-content',
    wordBreak: 'normal',
    overflowWrap: 'break-word',
    backgroundColor,
    color: textColor,
    fontSize: '14px',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    boxSizing: 'border-box',
    display: 'inline-block',
    position: 'relative',
    border: borderColor !== 'transparent' ? `2px solid ${borderColor}` : 'none',
    transition: 'all 0.3s ease',
    animation,
    '& p': {
      margin: '0 0 8px 0',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& ul, & ol': {
      margin: '8px 0',
      paddingLeft: '20px',
    },
    '& li': {
      margin: '4px 0',
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '12px 0 8px 0',
      fontWeight: 600,
    },
    '& code': {
      backgroundColor: isUser ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
      padding: '2px 4px',
      borderRadius: '3px',
      fontSize: '13px',
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor: isUser ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
      padding: '8px',
      borderRadius: '4px',
      overflow: 'auto',
      margin: '8px 0',
    },
    '& blockquote': {
      borderLeft: `3px solid ${isUser ? 'rgba(255, 255, 255, 0.5)' : '#ddd'}`,
      margin: '8px 0',
      paddingLeft: '12px',
      fontStyle: 'italic',
    },
    '@keyframes pulse': {
      '0%': {
        boxShadow: `0 0 0 0 ${borderColor}40`,
      },
      '70%': {
        boxShadow: `0 0 0 10px ${borderColor}00`,
      },
      '100%': {
        boxShadow: `0 0 0 0 ${borderColor}00`,
      },
    },
  };
});

const MessageBubbleContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  '&:hover .copy-icon': {
    opacity: 1,
  },
}));

const CopyIcon = styled(IconButton)(({ theme }) => ({
  width: '20px',
  height: '20px',
  minWidth: '20px',
  minHeight: '20px',
  backgroundColor: 'rgb(193, 193, 193)',
  color: 'rgb(96, 96, 96)',
  borderRadius: '50%',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
  opacity: 0,
  transition: 'all 0.2s ease-in-out',
  position: 'absolute',
  top: '8px',
  right: '8px',
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'rgb(175, 175, 175)',
    transform: 'scale(1.05)',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.19)',
  },
}));

export const MessageCard: React.FC<MessageCardProps> = memo(({
  variant,
  message,
  suggestions,
  onCopy,
}) => {
  const [copied, setCopied] = React.useState(false);

  const getAvatarContent = () => {
    if (variant === 'assistant') {
      return (
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          <SmartToy sx={{ fontSize: 20 }} />
        </Avatar>
      );
    }
    if (variant === 'system') {
      return (
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'warning.main' }}>
          <BuildIcon sx={{ fontSize: 18, color: 'white' }} />
        </Avatar>
      );
    }
    return (
      <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
        <PersonOutlineOutlined sx={{ fontSize: 20 }} />
      </Avatar>
    );
  };

  const handleCopy = useCallback(() => {
    if (onCopy) {
      const plainText = message.content.replace(/<[^>]*>/g, '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
      onCopy(plainText);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  }, [message.content, onCopy]);

  const isUser = variant === 'user';

  return (
    <MessageContainer isUser={isUser}>
      <MessageRow isUser={isUser}>
        {getAvatarContent()}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <MessageBubbleContainer>
            <MessageBubble
              isUser={isUser}
              processingStatus={message.processingType}
              sx={message.bgcolor ? { backgroundColor: message.bgcolor } : {}}
            >
              {variant === 'assistant' ? (
                <MarkupMAIQ content={message.content} />
              ) : (
                message.content
              )}
            </MessageBubble>
            
            {variant === 'assistant' && onCopy && (
              <CopyIcon
                className="copy-icon"
                onClick={handleCopy}
                size="small"
                aria-label="Copy message"
                sx={{ color: 'white' }}
              >
                {copied ? (
                  <Box sx={{ color: 'success.main', fontSize: 12 }}>✓</Box>
                ) : (
                  <ContentCopy sx={{ fontSize: 12 }} />
                )}
              </CopyIcon>
            )}
          </MessageBubbleContainer>
        </Box>
      </MessageRow>
    </MessageContainer>
  );
});

MessageCard.displayName = 'MessageCard';

